import {
  Loader2,
  FileText,
  ExternalLink,
  GitCommit,
  GitPullRequest,
  TestTube,
  Rocket,
  Link as LinkIcon,
} from "lucide-react";
import { WorkOrderDto } from "@shared/types/WorkOrderDto";
import { CommitCard } from "./CommitCard";
import { PullRequestCard } from "./PullRequestCard";
import { TestCard } from "./TestCard";
import { DeploymentCard } from "./DeploymentCard";
import { WorkSection } from "./WorkSection";

interface WorkOrderDetailsProps {
  workOrder: WorkOrderDto | null;
  isLoading: boolean;
}

export function WorkOrderDetails({
  workOrder,
  isLoading,
}: WorkOrderDetailsProps) {
  if (isLoading) {
    return (
      <div className="border border-border/50 rounded-lg p-8 text-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
        <p className="text-muted-foreground mt-2">Loading work details...</p>
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="border border-border/50 rounded-lg p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
          <FileText className="h-8 w-8 text-muted-foreground/50" />
        </div>
        <p className="font-medium">No work order linked</p>
        <p className="text-sm text-muted-foreground mt-1">
          This invoice is not linked to a work order yet
        </p>
      </div>
    );
  }

  const allCommits = workOrder.workUnits?.flatMap((wu) => wu.commits) ?? [];
  const allPRs = workOrder.workUnits?.flatMap((wu) => wu.pullRequests) ?? [];
  const allTests = workOrder.workUnits?.flatMap((wu) => wu.tests) ?? [];
  const allDeployments = workOrder.workUnits?.flatMap((wu) => wu.deployments) ?? [];
  const allEnvLinks = workOrder.workUnits?.reduce((acc, wu) => {
    return { ...acc, ...wu.environmentsLinks };
  }, {} as Record<string, string>) ?? {};

  const hasEnvironmentLinks = Object.keys(allEnvLinks).length > 0;
  const hasWorkUnits =
    allCommits.length || allPRs.length || allTests.length || allDeployments.length;

  return (
    <div className="border border-border/50 rounded-lg p-5">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-lg font-semibold">
          Project: {workOrder.project.name}
        </h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        {workOrder.project.scope}
      </p>

      {hasEnvironmentLinks && (
        <div className="mb-6 p-4 rounded-lg bg-muted/30 border border-border/50">
          <div className="flex items-center gap-2 mb-3">
            <LinkIcon className="h-4 w-4 text-muted-foreground" />
            <h3 className="font-medium text-sm">Environment Links</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(allEnvLinks).map(([name, url]) => (
              <a
                key={name}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-background border border-border/50 text-sm hover:border-primary/50 transition-colors"
              >
                {name}
                <ExternalLink className="h-3 w-3" />
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-6">
        <WorkSection
          title="Commits"
          icon={GitCommit}
          iconColor="text-blue-500"
          count={allCommits.length}
        >
          {allCommits.map((commit, idx) => (
            <CommitCard key={commit.shasum || idx} commit={commit} />
          ))}
        </WorkSection>

        <WorkSection
          title="Pull Requests"
          icon={GitPullRequest}
          iconColor="text-purple-500"
          count={allPRs.length}
        >
          {allPRs.map((pr, idx) => (
            <PullRequestCard key={pr.link || idx} pr={pr} />
          ))}
        </WorkSection>

        <WorkSection
          title="Tests"
          icon={TestTube}
          iconColor="text-cyan-500"
          count={allTests.length}
        >
          {allTests.map((test, idx) => (
            <TestCard key={test.link || idx} test={test} />
          ))}
        </WorkSection>

        <WorkSection
          title="Deployments"
          icon={Rocket}
          iconColor="text-green-500"
          count={allDeployments.length}
        >
          {allDeployments.map((deployment, idx) => (
            <DeploymentCard key={deployment.link || idx} deployment={deployment} />
          ))}
        </WorkSection>

        {!hasWorkUnits && (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-muted-foreground/50" />
            </div>
            <p className="font-medium">No work units linked yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Work details will appear here once they are associated with this
              invoice
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
